import { HttpResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { StockWriteOffService } from './stock-write-off.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router'; // 👈 Import Router
import { ToastrService } from 'ngx-toastr';
import { ExcelUploadComponent } from '../../../shared/excel-upload/excel-upload.component'; // 👈 Import ExcelUploadComponent
import { environment } from '../../../environments/environment'; // 👈 Import environment

interface StockWriteOff {
  id: string;
  writeOffNumber: string;
  status: string;
  totalQty: number | null;
  totalValue: number | null;
  // Add other properties if needed for the component logic
  storeId?: string; // Adding common relational fields for completeness
  locationId?: string;
}

@Component({
  selector: 'app-stock-write-off-list',
  standalone: true,
  // NOTE: You must ensure StockWriteOffService, CommonModule, FormsModule, RouterModule, and ToastrService are accessible.
  imports: [CommonModule, FormsModule, RouterModule, ExcelUploadComponent], // 👈 Ensure ExcelUploadComponent is added to imports
  styleUrls: ['./stock-write-off.scss'],
  templateUrl: './stock-write-off.html',
})
export class StockWriteOffListComponent implements OnInit {
  private stockWriteOffService = inject(StockWriteOffService);
  // Injecting ToastrService for error/success notifications
  private toastr = inject(ToastrService);

  private router = inject(Router); // 👈 Inject Router

  writeOffList: any[] = [];
  writeOffLoading = false;
  currentPage = 1;
  pageSize = 10;
  pageCount = 1;
  searchTerm = '';

  // URL for the Excel Upload component
  uploadUrl: string = `${environment.apiUrl}/stock-write-off/upload`;

  ngOnInit(): void {
    this.loadWriteOffs();
  }

  // --- Excel Upload & Template Download Logic ---

  downloadTemplate() {
    this.stockWriteOffService.downloadTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'stock_write_off_template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Template downloaded successfully.');
      },
      error: (err) => {
        console.error('Template download error:', err);
        this.toastr.error('Failed to download template.');
      },
    });
  }

  onUploadSuccess(res: any) {
    const data = res?.body || res;

    // Using the specific response structure provided by the user
    if (data && data.items && Array.isArray(data.items)) {
      this.toastr.success(data.message || 'Excel processed successfully.');

      // Navigate to create screen, passing the structured data via state
      this.router.navigate(['/stock-write-off/create'], { state: { uploadedData: data } });
    } else {
      this.toastr.warning('Uploaded Excel file was empty or missing items array.');
    }
  }

  onUploadError(error: any) {
    console.error('Excel upload error:', error);
    const errorMessage = error?.error?.message || error?.message || 'Failed to process Excel file.';
    this.toastr.error(errorMessage, 'Upload Error');
  }

  // --- Data Loading and Pagination ---

  /**
   * Fetches the list of stock write-offs from the service.
   */
  loadWriteOffs() {
    this.writeOffLoading = true;

    this.stockWriteOffService
      .getStockWriteOffs(
        { page: this.currentPage, limit: this.pageSize },
        this.searchTerm
      )
      .subscribe({
        // Using the object syntax for robust error handling
        next: (res: any) => {
          this.writeOffList = res?.data || [];
          console.log('Stock write-offs loaded:', this.writeOffList);
          this.pageCount = res.pageCount || 1;
          this.writeOffLoading = false;
        },
        error: (err) => {
          console.error('Error loading stock write-offs:', err);
          this.writeOffList = []; // Clear list on failure
          this.writeOffLoading = false;
          this.pageCount = 1;
          this.toastr.error('Failed to load stock write-offs.');
        },
      });
  }
  searchWriteOffByNumber(number: string) {
    this.writeOffLoading = true;
    this.stockWriteOffService.searchWriteOffsByNumber(number).subscribe({
      next: (res: any) => {
        // Assuming the search endpoint returns the data directly or in 'data'
        this.writeOffList = Array.isArray(res)
          ? res
          : res?.data || (res ? [res] : []);

        // When using the search-by-number API, pagination details are often irrelevant
        this.currentPage = 1;
        this.pageCount = 1;
        this.writeOffLoading = false;
      },
      error: (err) => {
        console.error('Error searching stock write-off by number:', err);
        this.writeOffList = [];
        this.writeOffLoading = false;
        this.toastr.error('No write-off found for the specified number.');
      },
    });
  }

  /**
   * Changes the current page and reloads the data.
   */
  goToPage(page: number) {
    if (page < 1 || page > this.pageCount || page === this.currentPage) return;
    this.currentPage = page;
    this.loadWriteOffs();
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(this.pageCount - 1, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  /**
   * Resets to the first page and reloads data when page size changes.
   */
  pageSizeChange() {
    this.currentPage = 1;
    this.loadWriteOffs();
  }

  /**
   * Resets to the first page and reloads data when search term changes.
   */
  onSearch() {
    this.currentPage = 1; // Always reset to page 1 for a new search

    // Check if the search term is a specific number (e.g., more than a few characters)
    // You might want a more sophisticated check (e.g., regex for your number format)
    if (this.searchTerm && this.searchTerm.length >= 3) {
      // Use the new dedicated API for number search
      this.searchWriteOffByNumber(this.searchTerm.trim());
    } else {
      // Use the existing paginated list API for general search (or when cleared)
      this.loadWriteOffs();
    }
  }

  // --- Document Download Logic (Uncommented and Enabled) ---

  /**
   * Downloads the stock write-off document (e.g., PDF).
   */
  //   downloadWriteOff(id: string) {
  //     this.stockWriteOffService
  //       // Assuming 'downloadStockWriteOff' exists and returns an HttpResponse<Blob>
  //       .downloadStockWriteOff(id)
  //       .subscribe({
  //         next: (response: HttpResponse<Blob>) => {
  //           const blob = response.body as Blob;
  //           if (!blob) {
  //             this.toastr.error('Download failed: Document body is empty.');
  //             return;
  //           }
  //           const filename = this.extractFilename(response, id);

  //           // Create a temporary link element to trigger the download
  //           const url = window.URL.createObjectURL(blob);
  //           const a = document.createElement('a');
  //           a.href = url;
  //           a.download = filename;
  //           document.body.appendChild(a); // Append to body required for Firefox
  //           a.click();
  //           document.body.removeChild(a); // Clean up
  //           window.URL.revokeObjectURL(url);
  //           this.toastr.success(`Download of ${filename} started.`);
  //         },
  //         error: (err: any) => {
  //           console.error('Download error:', err);
  //           this.toastr.error('Failed to download the Stock Write-Off document.');
  //         },
  //       });
  //   }

  /**
   * Extracts the filename from the Content-Disposition header.
   */
  private extractFilename(response: HttpResponse<Blob>, id: string): string {
    const disposition = response.headers.get('Content-Disposition') || '';

    // Attempt to match RFC 6266 filename* (UTF-8)
    let match = disposition.match(/filename\*=([^;]+)/i);
    if (match && match[1]) {
      // Decode: filename*=UTF-8''filename.pdf -> filename.pdf
      return decodeURIComponent(match[1].split("''").pop() || '');
    }

    // Attempt to match standard filename (ASCII)
    match = disposition.match(/filename="?([^";]+)"?/i);
    if (match && match[1]) {
      return match[1];
    }

    // Default fallback filename
    return `stock-write-off-${id}.pdf`;
  }
}
